import React, { useState, useEffect, useRef } from "react";
import DragDrop from "../../DragDrop";
import { Pencil, Trash, WarningCircle } from "@phosphor-icons/react";
import axiosInstance from "../../../api/axiosInstance";
import PageLoader from "../../loader/PageLoader";
import ImageLoader from "../../loader/ImageLoader";
import {toast} from "react-toastify";
import { uploadImageToServer } from "../../../api/uploadImage";

const Spa = () => {
    const [formData, setFormData] = useState({
        name: "",
        type: "",
        price: "",
        description: "",
        photos: [],
    });
    const [showForm, setShowForm] = useState(false);
    const [spaServices, setSpaServices] = useState([]);
    const [editIndex, setEditIndex] = useState(null);
    const [uploadedImageKeys, setUploadedImageKeys] = useState([]);
    const [uploadedImages, setUploadedImages] = useState([]);
    const [removedImageKeys, setRemovedImageKeys] = useState([]);
    const [isPageLoading, setIsPageLoading] = useState(true);
    const hotelId = localStorage.getItem("hotelId");
    const [errors, setErrors] = useState({});
    const imageRequestRef = useRef(0);

    useEffect(() => {
        fetchSpaServices();
    }, [hotelId]);

    const fetchSpaServices = async () => {
        try {
            const response = await axiosInstance.get(
                `/api/v1/hotels/${hotelId}/spa-services`,
            );
            setSpaServices(response.data.data || []);
            setIsPageLoading(false);
            // console.log(response);
        } catch (error) {
            console.error("Failed to fetch spa services:", error);
            if(error.response.data.message==="Hotel's spa services' info not found"){
                setSpaServices([]);
            }
            setIsPageLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prevFormData) => ({ ...prevFormData, [name]: value }));
        setErrors((prevErrors) => ({ ...prevErrors, [name]: "" }));
    };

    const resetFormStates = () => {
        setFormData({
            name: "",
            type: "",
            price: "",
            description: "",
            photos: [],
        });
        setUploadedImageKeys([]);
        setUploadedImages([]);
        setRemovedImageKeys([]);
        setEditIndex(null);
        setErrors({});
    };

    const handleFileUpload = async (files) => {
        const fileArray = Array.from(files);
        // console.log("Files to upload:", fileArray);

        const placeholders = fileArray.map((file, index) => ({
            id: `temp_${Date.now()}_${index}`,
            isLoading: true,
            file: file,
        }));

        setUploadedImages((prevImages) => [...prevImages, ...placeholders]);

        const uploadPromises = placeholders.map(async (placeholder, index) => {
            try {
                // console.log(
                //     `Starting upload for file ${index}:`,
                //     placeholder.file.name,
                // );
                const uploadUrlResponse = await axiosInstance.get(
                    `/api/v1/images/upload-presigned-url?file_name=${placeholder.file.name}`,
                );
                const { key, url } = uploadUrlResponse.data;
                await uploadImageToServer(placeholder.file, url);
                // console.log(
                //     `Upload completed for file ${index}:`,
                //     placeholder.file.name,
                // );

                return {
                    id: placeholder.id,
                    key,
                    url: URL.createObjectURL(placeholder.file),
                    isLoading: false,
                };
            } catch (error) {
                console.error(`Error uploading image ${index}:`, error);
                return { ...placeholder, isLoading: false, error: true };
            }
        });

        const uploadedFiles = await Promise.all(uploadPromises);

        setUploadedImages((prevImages) =>
            prevImages.map(
                (img) => uploadedFiles.find((u) => u.id === img.id) || img,
            ),
        );

        const validFiles = uploadedFiles.filter((file) => !file.error);
        setUploadedImageKeys((prevKeys) => [
            ...prevKeys,
            ...validFiles.map((f) => ({ key: f.key })),
        ]);
        setErrors((prevErrors) => ({ ...prevErrors, photos: "" }));
    };

    const handleRemoveMediaFiles = (indexToRemove) => {
        setUploadedImages((prevImages) => {
            const newImages = prevImages.filter(
                (_, index) => index !== indexToRemove,
            );
            const removedImage = prevImages[indexToRemove];
            if (
                removedImage &&
                !removedImage.isLoading &&
                !removedImage.error
            ) {
                setRemovedImageKeys((prev) => [...prev, removedImage.key]);
            }
            return newImages;
        });
        setUploadedImageKeys((prevKeys) =>
            prevKeys.filter((_, index) => index !== indexToRemove),
        );
    };

    // Submit 
    const handleSaveSpaService = async (e) => {
        e.preventDefault();
        if (!validateForm()) {
            return;
        }
        try {
            const finalFormData = {
                ...formData,
                price: parseInt(formData.price),
                photos: [
                    ...(formData.photos
                        ? formData.photos.filter(
                              (photos) => !removedImageKeys.includes(photos),
                          )
                        : []),
                    ...uploadedImageKeys.map((photos) => photos.key),
                ],
            };

            if (editIndex !== null) {
                await axiosInstance.put(
                    `/api/v1/hotels/${hotelId}/spa-services/${spaServices[editIndex].id}`,
                    finalFormData,
                );
            } else {
                await axiosInstance.post(
                    `/api/v1/hotels/${hotelId}/spa-services`,
                    finalFormData,
                );
            }

            await fetchSpaServices();
            resetFormStates();
            setShowForm(false);
        } catch (error) {
            console.error("Failed to save spa service:", error);
            toast.error(error?.response?.data?.message || "Some Error Occured")
        }
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.name.trim()) newErrors.name = "Name is required";
        if (!formData.price) newErrors.price = "Price is required";
        if (!formData.type) newErrors.type = "type is required";
        if (!formData.description.trim())
            newErrors.description = "Description is required";
        // if (uploadedImages.length === 0)
        //     newErrors.photos = "At least one image is required";

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleEditClick = (index) => {
        const selectedService = spaServices[index];
        setFormData(selectedService);
        setShowForm(true);
        setEditIndex(index);
        setRemovedImageKeys([]);
        fetchServiceImages(selectedService.photos);
    };

    const fetchServiceImages = async (imageKeys = []) => {
        // Two edits in quick succession leave both batches of presigned-URL calls in
        // flight with no ordering guarantee, so a slower earlier response could
        // overwrite a newer one. Only the most recent request may set state.
        const requestId = ++imageRequestRef.current;
        const imagePromises = imageKeys.map(async (key) => {
            try {
                const response = await axiosInstance.get(
                    `/api/v1/images/download-presigned-url?key=${key}`,
                );
                const imageUrl = response.data.url;
                return { key, url: imageUrl };
            } catch (error) {
                console.error("Error fetching image URL:", error);
                return null;
            }
        });

        const images = await Promise.all(imagePromises);
        if (requestId !== imageRequestRef.current) return;
        setUploadedImages(images.filter((img) => img !== null));
    };

    const handleDeleteClick = async (index) => {
        try {
            const serviceId = spaServices[index].id;
            await axiosInstance.delete(
                `/api/v1/hotels/${hotelId}/spa-services/${serviceId}`,
            );
            await fetchSpaServices();
        } catch (error) {
            console.error("Error deleting spa service:", error);
        }
    };

    const trimImgName = (name) => {
        return name && name.length > 12
            ? name.substring(0, 12) + "..."
            : name || "";
    };

    return (
        <>
            {isPageLoading && <PageLoader />}
            <div className="flex w-full">
                <div className="border-r-[1.5px] px-4 w-[40%]">
                    <button
                        className={`h-[48px] ${
                            showForm
                                ? "bg-[#FFF2F2] text-[#FFBFC1] cursor-not-allowed"
                                : "bg-[#ff7378]"
                        } px-4 rounded-lg text-[16px] mb-3`}
                        onClick={() => {
                            setShowForm(true);
                            resetFormStates();
                        }}
                        disabled={showForm}
                    >
                        Add Spa Service
                    </button>

                    <div className="flex gap-2 flex-wrap">
                        {spaServices.length > 0
                            ? spaServices.map((service, index) => (
                                  <div
                                      className="border-[1.5px] rounded-md border-[#BFC4CC] bg-[#EEF0FB] flex gap-3 px-2 py-1 mb-2"
                                      key={index}
                                  >
                                      <p className="font-semibold">
                                          {service.name}
                                      </p>
                                      <div className="flex justify-end gap-1">
                                          <button
                                              className="rounded-md p-1 border-[1.5px] border-[#BFC4CC] h-[24px] w-[24px] bg-white"
                                              onClick={() =>
                                                  handleEditClick(index)
                                              }
                                          >
                                              <Pencil size={14} />
                                          </button>
                                          <button
                                              className="rounded-md p-1 border-[1.5px] border-[#BFC4CC] h-[24px] w-[24px] bg-white text-[#ff7378]"
                                              onClick={() =>
                                                  handleDeleteClick(index)
                                              }
                                          >
                                              <Trash size={14} />
                                          </button>
                                      </div>
                                  </div>
                              ))
                            : ""}
                    </div>
                </div>

                {showForm && (
                    <div className="w-[60%] px-4 self-start sticky top-0 max-h-[calc(100vh-80px)] overflow-y-auto">
                        <form onSubmit={handleSaveSpaService}>
                            <button
                                className="h-[48px] bg-[#ff7378] w-full rounded-lg text-[16px]"
                                type="submit"
                            >
                                {editIndex !== null
                                    ? "Update Spa Service"
                                    : "Add Spa Service"}
                            </button>
                            <div className="flex flex-col gap-2 my-3">
                                <div>
                                    <p>Name</p>
                                    <input
                                        type="text"
                                        placeholder="Enter service name"
                                        className="w-[70%] p-2 border-[1.5px] rounded-lg mt-1"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                    />{" "}
                                    {errors.name && (
                                        <span className="text-[#E47186] text-xs flex items-center gap-1">
                                            <WarningCircle className="text-sm" />
                                            {errors.name}
                                        </span>
                                    )}
                                </div>
                                <div className="flex w-full gap-2">
                                    <div className="w-1/2">
                                        <p>Price</p>
                                        <input
                                            type="number"
                                            className="border-[1.5px] rounded p-2 w-full"
                                            placeholder="Enter the price"
                                            name="price"
                                            value={formData.price}
                                            onChange={handleChange}
                                        />
                                        {errors.price && (
                                            <span className="text-[#E47186] text-xs flex items-center gap-1">
                                                <WarningCircle className="text-sm" />
                                                {errors.price}
                                            </span>
                                        )}
                                    </div>
                                    <div className="w-1/2">
                                        <p>Type</p>
                                        <select
                                            name="type"
                                            value={formData.type}
                                            onChange={handleChange}
                                            className="border-[1.5px] rounded p-2 w-full"
                                        >
                                            <option value="">
                                                Choose Type
                                            </option>
                                            <option value="Ayurvedic Abhyanga">
                                                Ayurvedic Abhyanga
                                            </option>
                                            <option value="Shirodhara">
                                                Shirodhara
                                            </option>
                                            <option value="Hot Stone Therapy">
                                                Hot Stone Therapy
                                            </option>
                                            <option value="Swedish Massage">
                                                Swedish Massage
                                            </option>
                                            <option value="Balinese Massage">
                                                Balinese Massage
                                            </option>
                                            <option value="Himalayan Salt Massage">
                                                Himalayan Salt Massage
                                            </option>
                                            <option value="Detox Mud Wraps">
                                                Detox Mud Wraps
                                            </option>
                                            <option value="Reflexology">
                                                Reflexology
                                            </option>
                                            <option value="Thai Massage">
                                                Thai Massage
                                            </option>
                                            <option value="Herbal Steam Bath">
                                                Herbal Steam Bath
                                            </option>
                                        </select>
                                        {errors.type && (
                                            <span className="text-[#E47186] text-xs flex items-center gap-1">
                                                <WarningCircle className="text-sm" />
                                                {errors.type}
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <div>
                                    <p>Additional Info</p>
                                    <textarea
                                        name="description"
                                        value={formData.description}
                                        onChange={handleChange}
                                        className="border-[1.5px] rounded-lg p-2 w-full"
                                        placeholder="Enter description of the massage"
                                        rows={5}
                                    ></textarea>
                                    {errors.description && (
                                        <span className="text-[#E47186] text-xs flex items-center gap-1">
                                            <WarningCircle className="text-sm" />
                                            {errors.description}
                                        </span>
                                    )}
                                </div>
                                <div>
                                    <p>Upload Media</p>
                                    <div>
                                        <DragDrop
                                            onFilesUploaded={handleFileUpload}
                                            uploadedImages={uploadedImages}
                                            onRemoveMediaFile={
                                                handleRemoveMediaFiles
                                            }
                                            acceptedFileTypes={{
                                                "image/*": [
                                                    ".jpeg",
                                                    ".png",
                                                    ".jpg",
                                                ],
                                            }}
                                            maxFiles={10}
                                            dragDropText="Drag and drop spa service images here"
                                        />
                                    </div>
                                    {errors.photos && (
                                        <span className="text-[#E47186] text-xs flex items-center gap-1">
                                            <WarningCircle className="text-sm" />
                                            {errors.photos}
                                        </span>
                                    )}
                                    <div>
                                        {uploadedImages.length > 0 && (
                                            <div className="flex gap-2 w-full mt-2 h-full flex-nowrap overflow-x-auto">
                                                {uploadedImages.map(
                                                    (photos, index) => (
                                                        <div
                                                            key={photos.id}
                                                            className="w-[158px] h-[142px] rounded-lg border-[1.5px] border-[#BFC4CC] flex-shrink-0 overflow-hidden"
                                                        >
                                                            {photos.isLoading ? (
                                                                <ImageLoader />
                                                            ) : photos.error ? (
                                                                <div className="w-full h-full flex items-center justify-center text-red-500">
                                                                    Upload
                                                                    failed
                                                                </div>
                                                            ) : (
                                                                <>
                                                                    <img
                                                                        src={
                                                                            photos.url
                                                                        }
                                                                        alt={`Spa Service ${
                                                                            index +
                                                                            1
                                                                        }`}
                                                                        className="w-full h-[110px] bg-cover"
                                                                    />
                                                                    <div className="flex justify-between items-center h-[32px] px-1 bg-[#EEF0FB]">
                                                                        <p>
                                                                            {trimImgName(
                                                                                photos.key,
                                                                            )}
                                                                        </p>
                                                                        <button
                                                                            className="rounded-md p-1 border-[1.5px] border-[#F8D9DE] text-[#DC4662] h-[24px] w-[24px] bg-white"
                                                                            onClick={() =>
                                                                                handleRemoveMediaFiles(
                                                                                    index,
                                                                                )
                                                                            }
                                                                            type="button"
                                                                        >
                                                                            <Trash
                                                                                size={
                                                                                    14
                                                                                }
                                                                            />
                                                                        </button>
                                                                    </div>
                                                                </>
                                                            )}
                                                        </div>
                                                    ),
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </form>
                    </div>
                )}
            </div>
        </>
    );
};

export default Spa;
